import { z } from 'zod';

/**
 * Adéquation automatique (docs/INGESTION_EMAIL.md § Adéquation automatique).
 * C'est une aide au tri : aucune décision n'en découle, la note reste humaine.
 */
export const VERDICTS_CRITERE = ['conforme', 'partiel', 'manquant', 'indetermine'] as const;
export type VerdictCritere = (typeof VERDICTS_CRITERE)[number];

export const ADEQUATIONS = ['conforme', 'partiel', 'non_conforme', 'a_evaluer'] as const;
export type Adequation = (typeof ADEQUATIONS)[number];

export interface ResultatCritere {
  critereId: string;
  libelle: string;
  type: 'eliminatoire' | 'souhaitable';
  verdict: VerdictCritere;
  /** Extrait du CV qui justifie le verdict, vide si rien n'a été trouvé. */
  extrait: string | null;
}

export const syntheseAutoSchema = z.object({
  pointsForts: z.array(z.string()).max(3),
  vigilance: z.array(z.string()).max(3),
  experienceAnnees: z.number().nullable(),
  criteres: z.array(
    z.object({
      critereId: z.string(),
      libelle: z.string(),
      type: z.enum(['eliminatoire', 'souhaitable']),
      verdict: z.enum(VERDICTS_CRITERE),
      extrait: z.string().nullable(),
    }),
  ),
});
export type SyntheseAuto = z.infer<typeof syntheseAutoSchema>;

/**
 * Verdict global : un critère éliminatoire manquant suffit à marquer
 * « non conforme », sans écarter personne — le tri reste à l'humain.
 */
export function resumerAdequation(criteres: readonly ResultatCritere[]): Adequation {
  if (criteres.length === 0) return 'a_evaluer';
  const eliminatoires = criteres.filter((c) => c.type === 'eliminatoire');
  if (eliminatoires.some((c) => c.verdict === 'manquant')) return 'non_conforme';

  const evalues = criteres.filter((c) => c.verdict !== 'indetermine');
  if (evalues.length === 0) return 'a_evaluer';

  const conformes = evalues.filter((c) => c.verdict === 'conforme').length;
  if (conformes === evalues.length) return 'conforme';
  if (evalues.some((c) => c.verdict === 'manquant') && conformes === 0) return 'non_conforme';
  return 'partiel';
}

export const COULEURS_ADEQUATION: Record<Adequation, string> = {
  conforme: '#16a34a',
  partiel: '#ca8a04',
  non_conforme: '#dc2626',
  a_evaluer: '#9ca3af',
};

export const LIBELLES_ADEQUATION: Record<Adequation, string> = {
  conforme: 'Conforme',
  partiel: 'Partiellement conforme',
  non_conforme: 'Non conforme',
  a_evaluer: 'À évaluer',
};
